<template>
  <div
    class="relative z-20 bg-ink"
    data-testid="TheNavigation"
  >
    <div
      v-click-away="closeMenu"
      class="mx-auto max-w-screen-xl px-4"
    >
      <!-- Top bar: logo (left) · centered primary nav (desktop) · actions/burger (right) -->
      <div class="grid h-16 grid-cols-[auto_1fr_auto] items-center md:h-20 md:grid-cols-[1fr_auto_1fr]">
        <!-- Left: logo -->
        <router-link
          :to="$i18nRoute({ name: 'home' })"
          class="shrink-0 justify-self-start"
          data-testid="logoAnchor"
        >
          <img
            :src="$asset('/images/branding/hypenews/logo/logo_white.svg')"
            class="block h-10 w-auto md:h-12"
            alt="hype.news"
          >
        </router-link>

        <!-- Center: primary navigation (desktop only) -->
        <nav class="hidden items-center justify-center gap-1 md:flex">
          <router-link
            :to="$i18nRoute({ name: 'home' })"
            class="px-3 py-2 text-sm text-white/80 transition-colors hover:text-white"
            exact-active-class="!text-white font-semibold"
          >
            {{ $t('components.top_nav.read_news') }}
          </router-link>
          <template v-if="!isLoggedIn">
            <router-link
              :to="$i18nRoute({ name: 'for-brands' })"
              class="px-3 py-2 text-sm text-white/80 transition-colors hover:text-white"
              active-class="!text-white font-semibold"
            >
              {{ $t('components.top_nav.for_brands') }}
            </router-link>
            <router-link
              :to="$i18nRoute({ name: 'for-journalists' })"
              class="px-3 py-2 text-sm text-white/80 transition-colors hover:text-white"
              active-class="!text-white font-semibold"
            >
              {{ $t('components.top_nav.for_journalists') }}
            </router-link>
          </template>
        </nav>

        <!-- Right: actions (desktop) + burger (mobile) -->
        <div class="flex items-center justify-end gap-1">
          <div class="hidden items-center gap-1 md:flex">
            <newsroom-search-dropdown class="mr-1" />
            <the-language-switcher />
            <nav-auth-button />
            <create-story-button v-if="isLoggedIn" />
            <nav-dashboard-button />
          </div>

          <button
            type="button"
            class="flex items-center p-2 text-white md:hidden"
            :aria-expanded="isMenuActive ? 'true' : 'false'"
            aria-label="Toggle menu"
            @click="toggleMenu"
          >
            <i
              :class="isMenuActive ? 'hf-x' : 'hf-menu'"
              class="hf hf-2x"
            />
          </button>
        </div>
      </div>

      <!-- Mobile dropdown menu -->
      <div
        v-show="isMenuActive"
        class="absolute inset-x-0 top-full flex flex-col items-stretch bg-ink py-2 text-center shadow-lg md:hidden"
      >
        <nav-dashboard-button />
        <router-link
          :to="$i18nRoute({ name: 'home' })"
          class="px-4 py-3 text-base text-white/80 transition-colors hover:text-white md:px-3 md:py-2 md:text-sm"
          exact-active-class="!text-white font-semibold"
        >
          {{ $t('components.top_nav.read_news') }}
        </router-link>
        <template v-if="!isLoggedIn">
          <router-link
            :to="$i18nRoute({ name: 'for-brands' })"
            class="px-4 py-3 text-base text-white/80 transition-colors hover:text-white md:px-3 md:py-2 md:text-sm"
            active-class="!text-white font-semibold"
          >
            {{ $t('components.top_nav.for_brands') }}
          </router-link>
          <router-link
            :to="$i18nRoute({ name: 'for-journalists' })"
            class="px-4 py-3 text-base text-white/80 transition-colors hover:text-white md:px-3 md:py-2 md:text-sm"
            active-class="!text-white font-semibold"
          >
            {{ $t('components.top_nav.for_journalists') }}
          </router-link>
        </template>
        <nav-auth-button />
        <create-story-button v-if="isLoggedIn" />
        <div class="my-2 border-t border-white/10" />
        <div class="flex justify-center py-2">
          <newsroom-search-dropdown />
        </div>
        <div class="flex justify-center py-2">
          <the-language-switcher />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CreateStoryButton from './core/CreateStoryButton'
import NavAuthButton from './navigation/NavAuthButton'
import TheLanguageSwitcher from '@/shared/js/components/core/TheLanguageSwitcher'
import NavDashboardButton from './navigation/NavDashboardButton/Index'
import NewsroomSearchDropdown from '@/components/storySearch/NewsroomSearchDropdown'

/**
 * The main navigation element
 * @module TheNavigation
 */
export default {
  components: {
    CreateStoryButton,
    NavAuthButton,
    NavDashboardButton,
    TheLanguageSwitcher,
    NewsroomSearchDropdown
  },

  data () {
    return {
      isMenuActive: false
    }
  },

  watch: {
    '$route.name': 'closeMenu'
  },

  mounted () {
    // TODO: Very legacy. Needs to be moved away to a separate modal component
    const cb = (e) => {
      if (e.keyCode === 27) {
        this.closeAllAuthModals()
      }
    }
    document.addEventListener('keydown', cb)
    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('keydown', cb)
    })
  },

  methods: {
    closeMenu (el) {
      this.isMenuActive = false
    },

    toggleMenu () {
      this.isMenuActive = !this.isMenuActive
    }
  }
}
</script>
